/**
 * EmailCampaignListPage.js - 邮件营销活动管理页
 * 类似 Shopify 的 Email Marketing 功能
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Space,
  Tag,
  Popconfirm,
  message,
  Typography,
  Tooltip,
  Select,
  Row,
  Col,
  Divider,
  Empty,
  List,
  Avatar,
  Statistic,
  Progress,
  Steps,
  Radio,
  Collapse,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  SendOutlined,
  UserOutlined,
  TeamOutlined,
  MailOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  SearchOutlined,
  SettingOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import { adminEmailCampaignApi, adminCustomerSegmentApi, adminSettingApi } from '../../api/index';
import RichTextEditor from '../../components/RichTextEditor';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { Panel } = Collapse;

// 状态映射
const STATUS_CONFIG = {
  draft: { color: 'default', label: '草稿', icon: <ClockCircleOutlined /> },
  scheduled: { color: 'blue', label: '已计划', icon: <ClockCircleOutlined /> },
  sending: { color: 'processing', label: '发送中', icon: <MailOutlined /> },
  completed: { color: 'success', label: '已完成', icon: <CheckCircleOutlined /> },
  failed: { color: 'error', label: '失败', icon: <CloseCircleOutlined /> },
};

export default function EmailCampaignListPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [sendingModalVisible, setSendingModalVisible] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [viewingCampaign, setViewingCampaign] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [segments, setSegments] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [sendStats, setSendStats] = useState({});
  const [sendLogs, setSendLogs] = useState([]);
  const [recipientType, setRecipientType] = useState('segment');
  const [form] = Form.useForm();
  // 预览和发送测试
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [testEmailModalVisible, setTestEmailModalVisible] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testEmailLoading, setTestEmailLoading] = useState(false);

  // 拉取营销活动列表
  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminEmailCampaignApi.list();
      setCampaigns(Array.isArray(data) ? data : []);
    } catch (err) {
      message.error('加载营销活动列表失败：' + (err.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  }, []);

  // 拉取客户细分列表
  const fetchSegments = useCallback(async () => {
    try {
      const data = await adminCustomerSegmentApi.list();
      setSegments(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('加载客户细分列表失败', err);
    }
  }, []);

  // 拉取邮件模板列表
  const fetchEmailTemplates = useCallback(async () => {
    try {
      const data = await adminSettingApi.getEmailTemplates();
      setEmailTemplates(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('加载邮件模板列表失败', err);
    }
  }, []);

  useEffect(() => {
    fetchCampaigns();
    fetchSegments();
    fetchEmailTemplates();
  }, [fetchCampaigns, fetchSegments, fetchEmailTemplates]);

  // 打开新建 Modal
  const handleCreate = () => {
    setEditingCampaign(null);
    setRecipientType('segment');
    form.setFieldsValue({
      name: '',
      subject: '',
      fromName: '',
      fromEmail: '',
      recipientType: 'segment',
      segmentId: undefined,
      customerIds: '[]',
      emailTemplateId: undefined,
      customSubject: '',
      customBody: '',
    });
    setModalVisible(true);
  };

  // 打开编辑 Modal
  const handleEdit = (campaign) => {
    setEditingCampaign(campaign);
    setRecipientType(campaign.recipientType || 'segment');
    form.setFieldsValue({
      name: campaign.name,
      subject: campaign.subject,
      fromName: campaign.fromName,
      fromEmail: campaign.fromEmail,
      recipientType: campaign.recipientType,
      segmentId: campaign.segmentId,
      customerIds: campaign.customerIds,
      emailTemplateId: campaign.emailTemplateId,
      customSubject: campaign.customSubject,
      customBody: campaign.customBody,
      sendInterval: campaign.sendInterval || 1000,
      batchSize: campaign.batchSize || 50,
    });
    setModalVisible(true);
  };

  // 关闭 Modal
  const handleModalClose = () => {
    setModalVisible(false);
    setEditingCampaign(null);
    form.resetFields();
  };

  // 保存营销活动
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const data = {
        ...values,
        recipientType,
        customerIds: values.customerIds || '[]',
      };

      if (editingCampaign) {
        await adminEmailCampaignApi.update(editingCampaign.id, data);
        message.success('营销活动已更新');
      } else {
        await adminEmailCampaignApi.create(data);
        message.success('营销活动已创建');
      }
      handleModalClose();
      fetchCampaigns();
    } catch (err) {
      if (err?.errorFields) return;
      message.error('保存失败：' + (err.message || '未知错误'));
    } finally {
      setSaving(false);
    }
  };

  // 删除营销活动
  const handleDelete = async (id) => {
    try {
      await adminEmailCampaignApi.delete(id);
      message.success('营销活动已删除');
      fetchCampaigns();
    } catch (err) {
      message.error('删除失败：' + (err.message || '未知错误'));
    }
  };

  // 查看详情
  const handleViewDetail = async (campaign) => {
    setViewingCampaign(campaign);
    setDetailModalVisible(true);
    try {
      const stats = await adminEmailCampaignApi.getStats(campaign.id);
      setSendStats(stats);
      const logs = await adminEmailCampaignApi.getLogs(campaign.id);
      setSendLogs(Array.isArray(logs) ? logs : []);
    } catch (err) {
      message.error('加载详情失败：' + (err.message || '未知错误'));
    }
  };

  // 发送邮件
  const handleSend = async (campaign) => {
    setViewingCampaign(campaign);
    // 先获取收件人数量
    try {
      const recipients = await adminEmailCampaignApi.getRecipients(campaign.id);
      const updatedCampaign = {
        ...campaign,
        totalRecipients: Array.isArray(recipients) ? recipients.length : 0,
      };
      setViewingCampaign(updatedCampaign);
      setSendingModalVisible(true);
    } catch (err) {
      message.error('获取收件人列表失败：' + (err.message || '未知错误'));
    }
  };

  // 确认发送
  const confirmSend = async () => {
    if (!viewingCampaign) return;
    setSending(true);
    try {
      await adminEmailCampaignApi.send(viewingCampaign.id);
      message.success('邮件发送任务已启动');
      setSendingModalVisible(false);
      fetchCampaigns();
    } catch (err) {
      message.error('发送失败：' + (err.message || '未知错误'));
    } finally {
      setSending(false);
    }
  };

  // 预览邮件
  const handlePreview = async (campaign) => {
    setPreviewLoading(true);
    setPreviewModalVisible(true);
    try {
      const data = await adminEmailCampaignApi.preview(campaign.id);
      setPreviewData(data);
    } catch (err) {
      message.error('预览失败：' + (err?.response?.data?.message || err.message || '未知错误'));
      setPreviewModalVisible(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  // 发送测试邮件
  const handleTestEmail = (campaign) => {
    setViewingCampaign(campaign);
    setTestEmail('');
    setTestEmailModalVisible(true);
  };

  const confirmTestEmail = async () => {
    if (!testEmail || !testEmail.trim()) {
      message.warning('请输入测试邮箱地址');
      return;
    }
    setTestEmailLoading(true);
    try {
      await adminEmailCampaignApi.sendTest(viewingCampaign.id, testEmail);
      message.success('测试邮件已发送到 ' + testEmail);
      setTestEmailModalVisible(false);
    } catch (err) {
      message.error('发送失败：' + (err?.response?.data?.message || err.message || '未知错误'));
    } finally {
      setTestEmailLoading(false);
    }
  };

  // 复制营销活动
  const handleCopy = async (campaign) => {
    try {
      const copied = await adminEmailCampaignApi.copy(campaign.id);
      message.success('活动已复制');
      fetchCampaigns();
    } catch (err) {
      message.error('复制失败：' + (err?.response?.data?.message || err.message || '未知错误'));
    }
  };

  const columns = [
    {
      title: '活动名称',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <div>
          <strong>{name}</strong>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>{record.subject || '-'}</Text>
        </div>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => {
        const config = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
        return (
          <Tag color={config.color} icon={config.icon}>
            {config.label}
          </Tag>
        );
      },
    },
    {
      title: '收件人类型',
      dataIndex: 'recipientType',
      key: 'recipientType',
      width: 100,
      render: (type) => {
        const typeMap = {
          all: '全部客户',
          segment: '客户细分',
          manual: '手动指定',
        };
        return typeMap[type] || type;
      },
    },
    {
      title: '发送统计',
      key: 'stats',
      width: 150,
      render: (_, record) => (
        <div style={{ fontSize: 12 }}>
          <div>总数：{record.totalRecipients || 0}</div>
          <div style={{ color: '#52c41a' }}>成功：{record.successCount || 0}</div>
          <div style={{ color: '#ff4d4f' }}>失败：{record.failedCount || 0}</div>
        </div>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (val) => val ? new Date(val).toLocaleString('zh-CN', { hour12: false }) : '-',
    },
    {
      title: '操作',
      key: 'actions',
      width: 250,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small" direction="vertical" style={{ width: '100%' }}>
          <Space size="small">
            <Tooltip title="预览邮件">
              <Button
                type="link"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => handlePreview(record)}
              >
                预览
              </Button>
            </Tooltip>
            <Tooltip title="发送测试">
              <Button
                type="link"
                size="small"
                icon={<SearchOutlined />}
                onClick={() => handleTestEmail(record)}
              >
                测试
              </Button>
            </Tooltip>
          </Space>
          <Space size="small">
            <Tooltip title="查看详情">
              <Button
                type="link"
                size="small"
                onClick={() => handleViewDetail(record)}
              >
                详情
              </Button>
            </Tooltip>
            {record.status === 'draft' && (
              <Tooltip title="发送邮件">
                <Button
                  type="link"
                  size="small"
                  icon={<SendOutlined />}
                  onClick={() => handleSend(record)}
                >
                  发送
                </Button>
              </Tooltip>
            )}
          </Space>
          <Space size="small">
            <Tooltip title="编辑">
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleEdit(record)}
              />
            </Tooltip>
            <Tooltip title="复制">
              <Button
                type="link"
                size="small"
                icon={<CopyOutlined />}
                onClick={() => handleCopy(record)}
              />
            </Tooltip>
            <Popconfirm
              title="确认删除该营销活动？"
              onConfirm={() => handleDelete(record.id)}
              okText="删除"
              cancelText="取消"
              okButtonProps={{ danger: true }}
            >
              <Tooltip title="删除">
                <Button
                  type="link"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                />
              </Tooltip>
            </Popconfirm>
          </Space>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <Title level={4} style={{ margin: 0 }}>
          <MailOutlined style={{ marginRight: 8 }} />
          邮件营销
        </Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          新建活动
        </Button>
      </div>

      <Card style={{ borderRadius: 8 }} bodyStyle={{ padding: 0 }}>
        <Table
          dataSource={campaigns}
          columns={columns}
          rowKey="id"
          loading={loading}
          scroll={{ x: 900 }}
          pagination={false}
          locale={{ emptyText: '暂无营销活动' }}
        />
      </Card>

      {/* 新建/编辑 Modal */}
      <Modal
        title={editingCampaign ? '编辑营销活动' : '新建营销活动'}
        open={modalVisible}
        onCancel={handleModalClose}
        onOk={handleSave}
        okText="保存"
        cancelText="取消"
        confirmLoading={saving}
        width={800}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 8 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="活动名称"
                rules={[{ required: true, message: '请输入活动名称' }]}
              >
                <Input placeholder="例：春季促销活动" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="subject"
                label="邮件主题"
                rules={[{ required: true, message: '请输入邮件主题' }]}
              >
                <Input placeholder="例：春季特惠，全场 8 折起！" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="fromName" label="发件人名称">
                <Input placeholder="例：YK2 商店" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="fromEmail" label="发件人邮箱">
                <Input placeholder="例：noreply@yk2store.com" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">收件人设置</Divider>

          <Form.Item name="recipientType" label="收件人类型">
            <Radio.Group
              value={recipientType}
              onChange={(e) => setRecipientType(e.target.value)}
            >
              <Radio value="segment">按客户细分</Radio>
              <Radio value="all">全部客户</Radio>
              <Radio value="manual">手动指定</Radio>
            </Radio.Group>
          </Form.Item>

          {recipientType === 'segment' && (
            <Form.Item
              name="segmentId"
              label="选择客户细分"
              rules={[{ required: true, message: '请选择客户细分' }]}
            >
              <Select placeholder="请选择客户细分" allowClear>
                {segments.map((seg) => (
                  <Option key={seg.id} value={seg.id}>
                    {seg.name} ({seg.customerCount || 0}人)
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          {recipientType === 'manual' && (
            <Form.Item
              name="customerIds"
              label="客户 ID 列表（JSON 格式）"
              rules={[{ required: true, message: '请输入客户 ID 列表' }]}
            >
              <TextArea rows={3} placeholder='例：[1, 2, 3]' />
            </Form.Item>
          )}

          <Divider orientation="left">内容设置</Divider>

          <Form.Item name="emailTemplateId" label="选择邮件模板">
            <Select placeholder="请选择邮件模板（可选）" allowClear>
              {emailTemplates.map((tpl) => (
                <Option key={tpl.id} value={tpl.id}>
                  {tpl.label || tpl.eventType}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="customSubject" label="自定义主题">
            <Input placeholder="可选，为空则使用上方邮件主题" />
          </Form.Item>

          <Form.Item
            name="customBody"
            label="自定义内容"
            extra="可选，为空则使用默认模板。支持 {{first_name}}、{{last_name}}、{{email}} 等变量"
          >
            <RichTextEditor placeholder="留空则使用默认模板。支持富文本，变量如 {{first_name}}" />
          </Form.Item>

          <Divider orientation="left">发送设置</Divider>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="sendInterval"
                label="发送间隔（毫秒）"
                initialValue={1000}
                extra="每批邮件发送后暂停的时间，避免被邮件服务器限流"
              >
                <Input type="number" placeholder="1000" min={0} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="batchSize"
                label="每批并发数量"
                initialValue={50}
                extra="每批发送的邮件数量"
              >
                <Input type="number" placeholder="50" min={1} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      {/* 详情 Modal */}
      <Modal
        title={
          <div>
            <EyeOutlined style={{ marginRight: 8 }} />
            {viewingCampaign?.name} - 发送详情
          </div>
        }
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={800}
      >
        {viewingCampaign && (
          <>
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={8}>
                <Statistic
                  title="总收件人"
                  value={viewingCampaign.totalRecipients || 0}
                  prefix={<UserOutlined />}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="发送成功"
                  value={viewingCampaign.successCount || 0}
                  valueStyle={{ color: '#52c41a' }}
                  prefix={<CheckCircleOutlined />}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="发送失败"
                  value={viewingCampaign.failedCount || 0}
                  valueStyle={{ color: '#ff4d4f' }}
                  prefix={<CloseCircleOutlined />}
                />
              </Col>
            </Row>

            <Progress
              percent={
                viewingCampaign.totalRecipients > 0
                  ? Math.round(
                      ((viewingCampaign.successCount || 0) /
                        viewingCampaign.totalRecipients) *
                        100
                    )
                  : 0
              }
              status={viewingCampaign.status === 'completed' ? 'success' : 'active'}
              style={{ marginBottom: 24 }}
            />

            <Divider />

            <Title level={5}>发送记录</Title>
            {sendLogs.length === 0 ? (
              <Empty description="暂无发送记录" />
            ) : (
              <List
                itemLayout="horizontal"
                dataSource={sendLogs.slice(0, 50)}
                renderItem={(log) => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={
                        log.status === 'sent' ? (
                          <Avatar icon={<CheckCircleOutlined />} style={{ backgroundColor: '#52c41a' }} />
                        ) : log.status === 'failed' ? (
                          <Avatar icon={<CloseCircleOutlined />} style={{ backgroundColor: '#ff4d4f' }} />
                        ) : (
                          <Avatar icon={<ClockCircleOutlined />} />
                        )
                      }
                      title={log.customerEmail}
                      description={
                        <span>
                          状态：
                          <Tag color={STATUS_CONFIG[log.status]?.color}>
                            {STATUS_CONFIG[log.status]?.label}
                          </Tag>
                          {log.sentAt && ` | 发送时间：${new Date(log.sentAt).toLocaleString('zh-CN')}`}
                          {log.errorMessage && <Text type="danger"> | {log.errorMessage}</Text>}
                        </span>
                      }
                    />
                  </List.Item>
                )}
              />
            )}
          </>
        )}
      </Modal>

      {/* 发送确认 Modal */}
      <Modal
        title={
          <div>
            <SendOutlined style={{ marginRight: 8 }} />
            确认发送邮件
          </div>
        }
        open={sendingModalVisible}
        onCancel={() => setSendingModalVisible(false)}
        onOk={confirmSend}
        okText="确认发送"
        cancelText="取消"
        confirmLoading={sending}
        okButtonProps={{ type: 'primary', danger: false }}
      >
        <div style={{ padding: '20px 0' }}>
          <p>
            确认要发送邮件给 <strong>{viewingCampaign?.totalRecipients || 0}</strong> 位收件人吗？
          </p>
          <p style={{ color: '#faad14' }}>
            注意：邮件发送可能需要较长时间，请耐心等待。
          </p>
        </div>
      </Modal>

      {/* 预览邮件 Modal */}
      <Modal
        title={
          <div>
            <EyeOutlined style={{ marginRight: 8 }} />
            预览邮件
          </div>
        }
        open={previewModalVisible}
        onCancel={() => setPreviewModalVisible(false)}
        footer={null}
        width={800}
      >
        {previewLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Text>加载中...</Text>
          </div>
        ) : previewData ? (
          <div>
            <div style={{ marginBottom: 16 }}>
              <Text strong>邮件主题：</Text>
              <Text>{previewData.subject}</Text>
            </div>
            <div style={{ marginBottom: 16 }}>
              <Text strong>示例收件人：</Text>
              <Text>{previewData.sampleRecipient}</Text>
            </div>
            <div style={{ marginBottom: 16 }}>
              <Text strong>收件人数量：</Text>
              <Text>{previewData.recipientCount} 人</Text>
            </div>
            <Divider />
            <div style={{ marginBottom: 8 }}>
              <Text strong>邮件内容预览：</Text>
            </div>
            <div
              style={{
                border: '1px solid #e8e8e8',
                borderRadius: 8,
                padding: 16,
                maxHeight: 400,
                overflow: 'auto',
                background: '#fafafa',
              }}
            >
              <div dangerouslySetInnerHTML={{ __html: previewData.content }} />
            </div>
          </div>
        ) : null}
      </Modal>

      {/* 发送测试邮件 Modal */}
      <Modal
        title={
          <div>
            <SearchOutlined style={{ marginRight: 8 }} />
            发送测试邮件
          </div>
        }
        open={testEmailModalVisible}
        onCancel={() => setTestEmailModalVisible(false)}
        onOk={confirmTestEmail}
        okText="发送测试"
        cancelText="取消"
        confirmLoading={testEmailLoading}
      >
        <div style={{ padding: '20px 0' }}>
          <p>输入测试邮箱地址，系统将发送一封测试邮件到此邮箱：</p>
          <Input
            placeholder="请输入邮箱地址"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            style={{ marginTop: 8 }}
          />
          <p style={{ marginTop: 16, color: '#999', fontSize: 12 }}>
            测试邮件会在主题前添加 [测试] 标识，方便识别。
          </p>
        </div>
      </Modal>
    </div>
  );
}
